import { DateRangeSelect, ISelectType, ModalFilters } from "@4uhub/lib4uhub";
import { Box, Stack, TextField } from "@mui/material";
import React, { Dispatch, SetStateAction, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

const DEFAULT_PERIOD_ID = "last_30_days";

// Mesma regra de "Últimos 30 dias" do DateRangeSelect: hoje e os 29 dias anteriores.
export const getDefaultPeriodRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 29);

  return { start, end };
};

export interface IFilterParams {
  CreatedStart: Date | null;
  CreatedEnd: Date | null;
  OriginalPatientEncounterNumber: string;
  NewPatientEncounterNumber: string;
  NewPrescriptionNumber: string;
  IndividualBeneficiaryIntegrationId: string;
  IndividualBeneficiaryName: string;
}

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

const translationPath = "page.4uBus.logs.patientEncounterPsTransferRequest.";

const fieldWrapperSx = {
  flex: "1 1 100%",
  minWidth: 0,
};

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);
  const { t } = useTranslation();

  const defaultPeriod = useMemo(
    () =>
      ({
        id: DEFAULT_PERIOD_ID,
        name:
          t("Lib4uhub.components.date_range.last_30_days") || "Últimos 30 dias",
        value: DEFAULT_PERIOD_ID,
      }) as ISelectType,
    [t],
  );

  // O seletor precisa ser controlado: sem value/onValueSelected ele nasce em "Hoje" e sobrescreve o
  // período aplicado na tela (que começa em 30 dias).
  const [period, setPeriod] = useState<ISelectType | null>(defaultPeriod);

  const periodChangeHandler = useCallback((option: ISelectType | null) => {
    if (option) setPeriod(option);
  }, []);

  const badgeCount = useMemo(() => {
    const initial = initialState.current;
    let count = 0;

    if (
      filterParams.IndividualBeneficiaryIntegrationId !==
      initial.IndividualBeneficiaryIntegrationId
    )
      count += 1;
    if (
      filterParams.IndividualBeneficiaryName !== initial.IndividualBeneficiaryName
    )
      count += 1;
    if (filterParams.NewPatientEncounterNumber !== initial.NewPatientEncounterNumber)
      count += 1;
    if (filterParams.NewPrescriptionNumber !== initial.NewPrescriptionNumber)
      count += 1;

    return count;
  }, [filterParams, initialState]);

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setPeriod(defaultPeriod);
  };

  return (
    <Stack spacing={0.75} sx={{ width: "100%" }}>
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
        <DateRangeSelect
          setStartDate={(date) => setFilter({ CreatedStart: date })}
          setEndDate={(date) => setFilter({ CreatedEnd: date })}
          setEnableFields={() => {}}
          value={period}
          onValueSelected={periodChangeHandler}
        />

        <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
          <TextField
            value={filter.OriginalPatientEncounterNumber}
            size="small"
            label={
              t(translationPath + "original_patient_encounter_number") ||
              "Nº Atendimento Original"
            }
            fullWidth
            onChange={(e) =>
              setFilter({ OriginalPatientEncounterNumber: e.target.value })
            }
          />
        </Box>

        <FiltersActions
          onFilter={filterHandler}
          onClean={cleanHandler}
          showCleanBtn={showCleanBtn}
        />

        <ModalFilters
          badgeCount={badgeCount}
          actionsDisabled={false}
          onFilter={filterHandler}
          onCleanFilter={cleanHandler}
          triggerVariant="button"
          triggerLabel={t(translationPath + "additionalFilters") || "Mais filtros"}
          triggerButtonVariant="contained"
        >
          <Stack
            direction="row"
            gap={1.5}
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ width: "100%" }}
          >
            <Box sx={fieldWrapperSx}>
              <TextField
                value={filter.IndividualBeneficiaryIntegrationId}
                size="small"
                label={
                  t(translationPath + "individual_beneficiary_id") ||
                  "ID do Beneficiário"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ IndividualBeneficiaryIntegrationId: e.target.value })
                }
              />
            </Box>

            <Box sx={fieldWrapperSx}>
              <TextField
                value={filter.IndividualBeneficiaryName}
                size="small"
                label={
                  t(translationPath + "individual_beneficiary_name") ||
                  "Nome do Beneficiário"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ IndividualBeneficiaryName: e.target.value })
                }
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: 180 }}>
              <TextField
                value={filter.NewPatientEncounterNumber}
                size="small"
                label={
                  t(translationPath + "new_patient_encounter_number") ||
                  "Nº Atendimento Novo (PA)"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ NewPatientEncounterNumber: e.target.value })
                }
              />
            </Box>

            <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: 180 }}>
              <TextField
                value={filter.NewPrescriptionNumber}
                size="small"
                label={
                  t(translationPath + "new_prescription_number") ||
                  "Nº Prescrição Nova (PA)"
                }
                fullWidth
                onChange={(e) =>
                  setFilter({ NewPrescriptionNumber: e.target.value })
                }
              />
            </Box>
          </Stack>
        </ModalFilters>
      </Stack>
    </Stack>
  );
};

export default memo(MacroFilter);
